import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import Seo from "../components/Seo";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_carmarket-preview/artifacts/vmguiuiy_logo%20v9%20use-02%282%29.png";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t("auth.welcome_toast"));
      navigate(next);
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] bg-slate-50 flex items-center justify-center px-4 py-10">
      <Seo title={t("auth.signin")} description={t("auth.signin_sub")} />
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-7 fade-in-up">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-11 h-11 rounded-xl border border-slate-200 overflow-hidden">
            <img src={LOGO_URL} alt={t("brand.name")} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-display font-extrabold text-base text-cm-primary">{t("brand.name")}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cm-accent">{t("brand.tagline")}</div>
          </div>
        </div>
        <h1 className="font-display font-extrabold text-2xl text-cm-primary">{t("auth.welcome_back")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("auth.signin_sub")}</p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{t("auth.email")}</span>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{t("auth.password")}</span>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
          </label>
          {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          <button data-testid="login-submit" disabled={loading} type="submit" className="w-full px-5 py-3 rounded-xl bg-cm-primary text-white font-medium disabled:opacity-60 flex items-center justify-center gap-2 press-scale hover:bg-cm-accent">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.signin")}
          </button>
        </form>

        <p className="mt-5 text-sm text-slate-500 text-center">
          {t("auth.no_account")} <Link data-testid="login-to-register" to="/register" className="text-cm-accent font-medium hover:underline">{t("auth.create_account")}</Link>
        </p>
      </div>
    </div>
  );
}
